import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormGroup, Validators, FormControl, ReactiveFormsModule, NonNullableFormBuilder } from '@angular/forms';
import { FormsService, FormValidator } from '../../../core/services/forms.service';
import { PasswordService } from '@app/core/services/password.service';
import { HttpErrorResponse } from '@angular/common/http';
import { RecaptchaService } from '../../../core/services/recaptcha.service';
import { WaitIndicatorService } from '../../../core/services/wait-indicator.service';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { Observable, firstValueFrom, of } from 'rxjs';
import { ConfirmService } from '../../../core/services/confirm.service';
import { AlertService } from '../../../core/services/alert.service';
import { ExternalNavService } from '../../../core/services/external-nav.service';
import { AccountService } from '@api';
import { ControlsOf } from '@app/core/functions/controls-of';
import { AsyncPipe } from '@angular/common';
import { WtDateInputComponent } from '../../../core/components/wt-date-input.component';
import { NumericOnlyDirective } from '../../../core/directives/numericOnly.directive';
import { WtAutofocusDirective } from '../../../core/directives/wt-autofocus.directive';
import { WtFocusableDirective } from '../../../core/directives/wt-focusable.directive';
import { WtValidateOnBlurDirective } from '../../../core/directives/wt-validate-on-blur.directive';
import { WtValidationStateDirective } from '../../../core/directives/wt-validation-state.directive';
import { WtValidationMessagesComponent, WtValidationMessageComponent } from '@app/core/components/wt-validation-errors.component';
import { UsernameRecoveryInfo } from '../models/username-recovery-info';
import { UsernameRecoveryService } from '../services/username-recovery.service';

type RegistrationError = HttpErrorResponse & {
  errors: string[];
};

function formValidation(group: FormGroup) {
  const errors = {};

  if (group.controls['password'].value !== group.controls['password2'].value) {
    group.controls['password2'].setErrors({
      mismatch: true,
    });
  }

  if (group.controls['email'].value !== group.controls['email2'].value) {
    group.controls['email2'].setErrors({
      mismatch: true,
    });
  }

  return errors;
}

@Component({
  template: `<h2 class="title">Register</h2>

    <p>Please fill out the following form to register on the site:</p>

    <form [formGroup]="registrationForm" class="form-horizontal col-sm-8" novalidate>
      <div class="form-group required" [wtValidationState]="validator.getControlStatus('firstname') | async">
        <label class="col-md-3 control-label" for="id_firstname"> First Name </label>
        <div class="col-md-9">
          <input
            formControlName="firstname"
            class="form-control"
            id="id_firstname"
            maxlength="50"
            name="firstname"
            placeholder="First Name"
            required="required"
            type="text"
            wtValidateOnBlur
            wtFocusable
            wtAutofocus
          />
          <wt-validation-messages [validator]="validator" controlName="firstname">
            <wt-validation-message name="required">This field is required</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('lastname') | async">
        <label class="col-md-3 control-label" for="id_lastname"> Last Name </label>
        <div class="col-md-9">
          <input
            formControlName="lastname"
            class="form-control"
            id="id_lastname"
            maxlength="50"
            name="lastname"
            placeholder="Last Name"
            required="required"
            type="text"
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="lastname">
            <wt-validation-message name="required">This field is required</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div
        class="form-group required"
        [wtValidationState]="validator.getControlStatus('username') | async"
        [class.has-success]="usernameVerified"
      >
        <label class="col-md-3 control-label" for="id_username"> Username </label>
        <div class="col-md-9">
          <input
            formControlName="username"
            class="form-control"
            id="id_username"
            maxlength="50"
            name="username"
            placeholder="Username"
            required="required"
            type="text"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            autocomplete="off"
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="username">
            <wt-validation-message name="required">This field is required</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('password') | async">
        <label class="col-md-3 control-label" for="id_password"> Password </label>
        <div class="col-md-9">
          <input
            formControlName="password"
            class="form-control"
            id="id_password"
            maxlength="50"
            name="password"
            placeholder="Enter your password"
            required="required"
            type="password"
            wtValidateOnBlur
            wtFocusable
          />
          <p class="help-block">{{ passwordMessage }}</p>
          <wt-validation-messages [validator]="validator" controlName="password">
            <wt-validation-message name="required">This field is required</wt-validation-message>
            <wt-validation-message name="password">Password requirements not met.</wt-validation-message>
            <wt-validation-message name="maxLength">Maximum length is 200.</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('password2') | async">
        <label class="col-md-3 control-label" for="id_password2"> Password (again) </label>
        <div class="col-md-9">
          <input
            formControlName="password2"
            class="form-control"
            id="id_password2"
            maxlength="50"
            name="password2"
            placeholder="Please re-enter your password for verification"
            required="required"
            type="password"
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="password2">
            <wt-validation-message name="mismatch">The passwords you entered do not match</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('email') | async">
        <label class="col-md-3 control-label" for="id_email"> Email </label>
        <div class="col-md-9">
          <input
            formControlName="email"
            class="form-control"
            id="id_email"
            maxlength="50"
            name="email"
            placeholder="Please enter your email address"
            required="required"
            [pattern]="pattern"
            type="email"
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="email">
            <wt-validation-message name="required">This field is required</wt-validation-message>
            <wt-validation-message name="pattern">Please enter a valid email address</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('email2') | async">
        <label class="col-md-3 control-label" for="id_email2"> Email (again) </label>
        <div class="col-md-9">
          <input
            formControlName="email2"
            class="form-control"
            id="id_email2"
            maxlength="50"
            name="email2"
            placeholder="Please re-enter your email address for verification"
            required="required"
            type="email"
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="email2">
            <wt-validation-message name="mismatch">The email addresses you entered do not match </wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('loanno') | async">
        <label class="col-md-3 control-label" for="id_loanno"> Loan Number </label>
        <div class="col-md-9">
          <input
            formControlName="loanno"
            class="form-control"
            id="id_loanno"
            maxlength="10"
            name="loanno"
            placeholder="Loan Number"
            required="required"
            type="text"
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="loanno">
            <wt-validation-message name="required">This field is required</wt-validation-message>
            <wt-validation-message name="minlength">Must be 10 digits</wt-validation-message>
            <wt-validation-message name="maxlength">Must be 10 digits</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('ssn') | async">
        <label class="col-md-3 control-label" for="id_ssn"> Social Security Number </label>
        <div class="col-md-9">
          <input
            formControlName="ssn"
            class="form-control"
            id="id_ssn"
            minlength="9"
            maxlength="9"
            name="ssn"
            placeholder="Please enter only digits with no hyphens or spaces"
            required="required"
            type="password"
            appNumericOnly
            wtValidateOnBlur
            wtFocusable
          />
          <wt-validation-messages [validator]="validator" controlName="ssn">
            <wt-validation-message name="required">This field is required</wt-validation-message>
            <wt-validation-message name="minlength">Must be 9 digits</wt-validation-message>
            <wt-validation-message name="maxlength">Must be 9 digits</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group required" [wtValidationState]="validator.getControlStatus('dob') | async">
        <label class="col-md-3 control-label" for="id_dob"> Date of Birth </label>
        <div class="col-md-9">
          <wt-date-input formControlName="dob" />
          <wt-validation-messages [validator]="validator" controlName="dob">
            <wt-validation-message name="required">This field is required</wt-validation-message>
          </wt-validation-messages>
        </div>
      </div>

      <div class="form-group">
        <div class="col-md-offset-3 col-md-9 checkbox">
          <div class="well">
            <label for="id_remember_this_browser">
              <input formControlName="remember_this_browser" id="id_remember_this_browser" name="remember_this_browser" type="checkbox" />
              <b>I am using a computer I trust</b>
            </label>
          </div>
        </div>
      </div>

      <div class="form-group">
        <div class="col-md-offset-3 col-md-9">
          <button class="btn btn-primary" (click)="register()">Register</button>
        </div>
      </div>
    </form> `,
  styles: `
    :not([has-success]) {
      .control-label {
        .required-input {
          color: #a94442;
        }
      }
    }

    .has-success {
      .control-label {
        .required-input {
          color: #3c763d;
        }
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WtValidationStateDirective,
    WtValidateOnBlurDirective,
    WtFocusableDirective,
    WtAutofocusDirective,
    WtValidationMessagesComponent,
    WtValidationMessageComponent,
    NumericOnlyDirective,
    WtDateInputComponent,
    AsyncPipe,
  ],
})
export class RegistrationAppComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private accountsService = inject(AccountService);
  private formsService = inject(FormsService);
  private passwordService = inject(PasswordService);
  private recaptchaService = inject(RecaptchaService);
  private waitIndicatorService = inject(WaitIndicatorService);
  private externalNav = inject(ExternalNavService);
  private confirmService = inject(ConfirmService);
  private alertService = inject(AlertService);
  private usernameRecoveryService = inject(UsernameRecoveryService);

  public registrationForm: FormGroup<
    ControlsOf<{
      firstname: string;
      lastname: string;
      username: string;
      password: string;
      password2: string;
      email: string;
      email2: string;
      loanno: string;
      ssn: string;
      dob: string;
      remember_this_browser: boolean;
    }>
  >;
  public validator: FormValidator;
  public usernameVerified = false;
  public passwordMessage: string;
  public pattern = '^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$';

  ngOnInit(): void {
    this.buildForm();
    this.passwordMessage = this.passwordService.getMessage();
  }

  async register() {
    this.validator.setSubmitted();

    if (!this.registrationForm.valid) {
      return;
    }

    this.waitIndicatorService.setWaiting();

    await firstValueFrom(
      this.recaptchaService.getNewRecaptchaToken('registration').pipe(
        switchMap((recaptchaToken) => {
          const formValue = this.registrationForm.getRawValue();

          return this.accountsService
            .accountRegisterCreate({
              registration: {
                firstname: formValue.firstname,
                lastname: formValue.lastname,
                username: formValue.username,
                password: formValue.password,
                email: formValue.email,
                loanno: formValue.loanno,
                ssn: formValue.ssn.replace(/-/g, ''),
                dob: formValue.dob,
                remember_this_browser: formValue.remember_this_browser,
                recaptcha_response: recaptchaToken,
              },
            })
            .pipe(
              tap((result) => {
                if (result.available_loans.length > 1) {
                  this.externalNav.go('/loanmanager/main/');
                } else {
                  this.externalNav.go('/loanmanager/loanoverview/');
                }
                this.waitIndicatorService.setReady();
              }),
              catchError((err: HttpErrorResponse) => {
                const foo = this.processRegistrationError(err, {
                  loanno: formValue.loanno,
                  ssn: formValue.ssn,
                  dob: formValue.dob,
                });
                this.waitIndicatorService.setReady();
                return foo;
              })
            );
        }),
        take(1)
      )
    );
  }

  public showError(formControlName: string): boolean {
    return !!this.registrationForm.get(formControlName)?.touched && !this.registrationForm.get(formControlName)?.valid;
  }

  public showPasswordTaken(): boolean {
    return this.registrationForm.controls['username'].touched && this.registrationForm.controls['username'].hasError('usernameTaken');
  }

  private buildForm(): void {
    this.registrationForm = this.fb.group(
      {
        firstname: this.fb.control('', Validators.required),
        lastname: this.fb.control('', Validators.required),
        username: this.fb.control('', Validators.required),
        password: this.fb.control('', [
          Validators.required,
          (x: FormControl) => this.passwordService.checkPasswordValidator(x),
          Validators.maxLength(200),
        ]),
        password2: this.fb.control('', [Validators.required, Validators.maxLength(200)]),
        email: this.fb.control('', Validators.required),
        email2: this.fb.control('', Validators.required),
        loanno: this.fb.control('', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]),
        ssn: this.fb.control('', [Validators.required, Validators.minLength(9)]),
        dob: this.fb.control('', Validators.required),
        remember_this_browser: this.fb.control(false),
      },
      { validators: [formValidation] }
    );
    this.validator = this.formsService.getFormValidator(this.registrationForm);
  }

  private processRegistrationError(err: HttpErrorResponse, info: UsernameRecoveryInfo): Observable<unknown> {
    if (err.status === 409) {
      const confirmation = this.confirmService.confirm('You have already registered. Would you like to have your username emailed to you?');

      confirmation.subscribe((result) => {
        if (result) {
          this.usernameRecoveryService.setCache(info);
          this.externalNav.go('/loanmanager/recover_username/');
        }
      });

      return confirmation;
    } else if (this.isRegistrationError(err.error)) {
      let errorMessage =
        'We were unable to find your account based on the loan number, social security number, ' +
        'and date of birth you entered. Please check your entries and try again.';

      if (err?.error?.errors?.length) {
        try {
          errorMessage = err.error.errors[0];
        } catch (e) {
          console.error(e);
        }
      }

      return this.alertService.alert(errorMessage);
    }

    return of(null);
  }

  private isRegistrationError(e: unknown): e is RegistrationError {
    return !!(e as RegistrationError)?.errors;
  }
}
